<script lang="ts">
  import type { ImagePreviewProps } from './types';
  // @ts-ignore
  import * as UTIF from 'utif';

  export let imageUrl: ImagePreviewProps['imageUrl'];
  export let alt = 'Uploaded image';
  export let onZoom: ImagePreviewProps['onZoom'] = () => {};
  export let file: File | null = null;

  let modalOpen = false;
  let imageError = false;
  let imageLoaded = false;
  let loadingTimeout: ReturnType<typeof setTimeout>;
  let tiffDataUrl: string | null = null;

  function openModal() {
    modalOpen = true;
    onZoom?.();
  }

  function closeModal() {
    modalOpen = false;
  }

  async function handleTiffLoad(file: File) {
    try {
      const buffer = await file.arrayBuffer();
      const ifds = UTIF.decode(buffer);
      if (ifds.length === 0) throw new Error('No IFDs found in TIFF');

      const ifd = ifds[0];
      UTIF.decodeImage(buffer, ifd);

      const rgba = UTIF.toRGBA8(ifd);
      const canvas = document.createElement('canvas');
      canvas.width = ifd.width;
      canvas.height = ifd.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      const imageData = new ImageData(new Uint8ClampedArray(rgba), ifd.width, ifd.height);
      ctx.putImageData(imageData, 0, 0);

      tiffDataUrl = canvas.toDataURL('image/png');
      imageLoaded = true;
      imageError = false;
    } catch (error) {
      console.error('TIFF decoding failed:', error);
      imageError = true;
    }
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }
  }

  function handleImageLoad() {
    console.log('Image loaded successfully:', imageUrl);
    imageLoaded = true;
    imageError = false;
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }
  }

  function handleImageError() {
    console.error('Failed to load image:', imageUrl);
    imageError = true;
    imageLoaded = false;
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }
  }

  $: fileExtension = file?.name.split('.').pop()?.toLowerCase();
  $: isTiff = fileExtension === 'tif' || fileExtension === 'tiff';

  // Set a timeout to detect if image is taking too long to load
  $: if (imageUrl && !imageLoaded && !imageError) {
    loadingTimeout = setTimeout(() => {
      if (!imageLoaded && !imageError) {
        console.warn('Image loading timeout:', imageUrl);
        imageError = true;
      }
    }, 10000); // 10 second timeout
  }

  // Handle TIFF files
  $: if (isTiff && file && !tiffDataUrl && !imageLoaded && !imageError) {
    handleTiffLoad(file);
  }
</script>

<div class="image-preview" role="button" tabindex="0">
  {#if !imageUrl}
    <div class="error-placeholder">
      <svg class="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
      </svg>
      <p class="text-gray-500 text-sm">No image available</p>
      <p class="text-gray-400 text-xs mt-1">Please select an image file first</p>
    </div>
  {:else if !imageLoaded && !imageError}
    <div class="loading-placeholder">
      <svg class="animate-spin w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-gray-500 text-sm">Loading image...</p>
    </div>
  {:else if imageError}
     <div class="error-placeholder">
       <svg class="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
       </svg>
       <p class="text-gray-500 text-sm">Failed to load image</p>
       <p class="text-gray-400 text-xs mt-1">The image format may not be supported or corrupted</p>
       <p class="text-gray-400 text-xs">Supported formats: PNG, JPG, GIF, BMP, WebP, TIFF</p>
     </div>
   {:else if imageLoaded}
     <button class="image-container" onclick={openModal} aria-label="Click to zoom image">
       <img
         src={tiffDataUrl || imageUrl}
         {alt}
         class="preview-image"
         onload={handleImageLoad}
         onerror={handleImageError}
         crossorigin="anonymous"
       />
      <span class="zoom-button" aria-hidden="true">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
        </svg>
      </span>
    </button>
  {/if}
</div>

{#if modalOpen}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal-overlay" onclick={(e) => e.target === e.currentTarget && closeModal()} onkeydown={(e) => e.key === 'Escape' && closeModal()} role="dialog" aria-modal="true" tabindex="-1">
    <div class="modal-content">
      <button
        class="close-button"
        onclick={closeModal}
        aria-label="Close modal"
      >
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
       <img
         src={tiffDataUrl || imageUrl}
         {alt}
         class="modal-image"
       />
    </div>
  </div>
{/if}

<style>
	.image-preview {
		@apply relative inline-block w-full max-w-2xl;
	}

	.image-container {
		@apply relative inline-block bg-transparent border-none p-0 cursor-pointer;
	}

	.preview-image {
		@apply w-full h-auto max-h-96 rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-200 object-contain border border-gray-200;
	}

	.loading-placeholder,
	.error-placeholder {
		@apply flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 w-full h-64;
	}

	.zoom-button {
		@apply absolute top-2 right-2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-1.5 shadow-md transition-all duration-200;
	}

	.zoom-button svg {
		@apply w-3.5 h-3.5 text-gray-600;
	}

  .modal-overlay {
    @apply fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50;
  }

  .modal-content {
    @apply relative max-w-4xl max-h-full p-4;
  }

  .close-button {
    @apply absolute top-2 right-2 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors duration-200 z-10;
  }

  .close-button svg {
    @apply w-6 h-6 text-gray-600;
  }

  .modal-image {
    @apply max-w-full max-h-full object-contain;
  }
</style>